import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../services/api';
import {
  X,
  FileText,
  Smartphone,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  AlertCircle
} from 'lucide-react';
import { AdminModalSurface } from './AdminModal';

export default function LogViewerModal({ userId, userName, onClose }) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
    // loadLogs intentionally follows the current account and page only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUserLogs(userId, page, 10);
      if (res.data.success) {
        setLogs(res.data.data.logs);
        setPagination(res.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
      alert('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const downloadLog = (log) => {
    const blob = new Blob([log.logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-${userId}-log-${log.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminModalSurface onClose={onClose}>
      <div className="bg-slate-800 w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] border border-white/10 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-8 border-b border-white/10 flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-white flex items-center gap-3">
              <FileText className="text-cyan-400" size={28} />
              {t('userManagement.logViewer.title')}
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              {t('userManagement.logViewer.subtitle', { name: userName })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-3 text-slate-500 hover:text-white transition-colors rounded-xl hover:bg-white/5"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              {t('userManagement.logViewer.loading')}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto text-slate-600 mb-4" size={48} />
              <p className="text-slate-500">{t('userManagement.logViewer.noLogs')}</p>
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className="bg-slate-900/50 rounded-2xl border border-white/5 p-6 hover:border-cyan-400/30 transition-all"
              >
                {/* Log Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-400/10 rounded-lg">
                      <FileText className="text-cyan-400" size={20} />
                    </div>
                    <div>
                      <p className="text-white font-bold">Log #{log.id}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(log.uploadedAt)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadLog(log)}
                    className="p-2 text-slate-500 hover:text-cyan-400 transition-colors"
                    title={t('userManagement.logViewer.download')}
                  >
                    <Download size={18} />
                  </button>
                </div>

                {/* Device Info */}
                {log.deviceInfo && (
                  <div className="mb-4 p-3 bg-slate-800/50 rounded-xl">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-2 flex items-center gap-2">
                      <Smartphone size={12} />
                      {t('userManagement.logViewer.deviceInfo')}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-slate-500">Model:</span>
                        <span className="text-white ml-2">{log.deviceInfo.model || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">OS:</span>
                        <span className="text-white ml-2">{log.deviceInfo.os || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">App Version:</span>
                        <span className="text-white ml-2">{log.appVersion || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Log Content Preview */}
                <div className="bg-black/30 rounded-xl p-4 font-mono text-xs text-slate-300 max-h-40 overflow-y-auto">
                  <pre className="whitespace-pre-wrap break-words">
                    {log.logs.substring(0, 500)}
                    {log.logs.length > 500 && '...'}
                  </pre>
                </div>

                {/* View Full Log Button */}
                {log.logs.length > 500 && (
                  <button
                    onClick={() => setSelectedLog(log)}
                    className="mt-3 text-cyan-400 text-xs hover:underline"
                  >
                    {t('userManagement.logViewer.viewFull')}
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="p-6 border-t border-white/10 flex justify-between items-center">
            <p className="text-sm text-slate-500">
              {t('userManagement.logViewer.showing', {
                start: (page - 1) * 10 + 1,
                end: Math.min(page * 10, pagination.total),
                total: pagination.total
              })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 bg-slate-700 rounded-xl text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-600 transition-all"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
                className="p-2 bg-slate-700 rounded-xl text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-600 transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Full Log Modal */}
      {selectedLog && (
        <AdminModalSurface onClose={() => setSelectedLog(null)}>
          <div className="bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-3xl border border-white/10 shadow-2xl flex flex-col">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h4 className="text-xl font-bold text-white">Full Log #{selectedLog.id}</h4>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-2 text-slate-500 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="bg-black/50 rounded-xl p-6 font-mono text-xs text-slate-300 whitespace-pre-wrap break-words">
                {selectedLog.logs}
              </pre>
            </div>
          </div>
        </AdminModalSurface>
      )}
    </AdminModalSurface>
  );
}
